import { useState } from 'react';
import { CreditCard, Cylinder, Mail, Wrench } from 'lucide-react';

import {
  useSendStatement,
  useSetBillingComms,
  useSetCustomerFeatures,
  useSetInvoiceEmails,
} from '@/hooks/admin/useAdminCustomers';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import {
  BillingCommsEditor,
  FeatureToggle,
  type StatementResult,
} from './billing-comms';
import ReturnedPaymentPanel from './ReturnedPaymentPanel';
import PhoneDirectoryPanel from './PhoneDirectoryPanel';
import CommsHistoryPanel from './CommsHistoryPanel';
import EdiSettingsCard from './EdiSettingsCard';
import EdiCrosswalkPanel from './EdiCrosswalkPanel';
import { CustomerDocumentsCard } from './PaymentDocsPanels';
import { GroupInheritanceBanner } from './GroupInheritanceBanner';
import { useCustomerGroup } from '@/hooks/admin/useCustomerGroup';

export default function BillingTab() {
  const { customer, refetch } = useCustomerDetailContext();

  // Member store of a Master-billing group → the accounting email, the
  // Accounting slot on invoice routing, invoice-email toggle, and
  // statements/paper are locked (managed on the master account).
  const { data: groupInfo } = useCustomerGroup(customer.id);
  const groupLock =
    groupInfo?.member_of && groupInfo.member_of.billing_mode === 'master'
      ? { masterName: groupInfo.member_of.master_name }
      : null;

  const features = useSetCustomerFeatures();
  const invoiceEmails = useSetInvoiceEmails();
  const billingComms = useSetBillingComms();
  const sendStatement = useSendStatement();

  const [statementResult, setStatementResult] = useState<StatementResult | null>(null);

  const afterMutate = () => refetch();

  return (
    <div className="space-y-5">
      {/* Member store of a Master-billing group → comms/payment settings
          inherit from the master; edits here become store overrides. */}
      <GroupInheritanceBanner customerId={customer.id} />

      {/* Tax ID / resale certificate vault + "request missing docs" email.
          (The payment profile lives on Overview, merged into the single
          "Account & payments" card — not duplicated here.) */}
      <CustomerDocumentsCard customerId={customer.id} />

      {/* EDI Orders & Invoicing — email-PO intake for chain customers */}
      <EdiSettingsCard customerId={customer.id} />
      <EdiCrosswalkPanel customerId={customer.id} />

      {/* Feature flags */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
        <h2 className="font-display text-sm font-semibold text-brix-ink">Features</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <FeatureToggle
            icon={<Cylinder size={14} />}
            label="Cylinder inventory"
            desc="Shows the /cylinders page + nav"
            enabled={customer.features?.cylinder_inventory !== false}
            saving={features.isPending}
            onChange={(v) =>
              features.mutate(
                { customer_id: customer.id, features: { cylinder_inventory: v } },
                { onSuccess: afterMutate },
              )
            }
          />
          <FeatureToggle
            icon={<Wrench size={14} />}
            label="Service requests"
            desc="Service ticket create + list"
            enabled={customer.features?.service_requests !== false}
            saving={features.isPending}
            onChange={(v) =>
              features.mutate(
                { customer_id: customer.id, features: { service_requests: v } },
                { onSuccess: afterMutate },
              )
            }
          />
          <FeatureToggle
            icon={<CreditCard size={14} />}
            label="Pay online"
            desc="Pay Now button on invoices"
            enabled={customer.features?.pay_online !== false}
            saving={features.isPending}
            onChange={(v) =>
              features.mutate(
                { customer_id: customer.id, features: { pay_online: v } },
                { onSuccess: afterMutate },
              )
            }
          />
          <FeatureToggle
            icon={<Mail size={14} />}
            label="Invoice emails"
            desc={
              groupLock
                ? `Locked ON — invoices always email ${groupLock.masterName}'s Accounting address`
                : 'Email new invoices hourly (opt-in)'
            }
            enabled={groupLock ? true : customer.invoice_emails_enabled === true}
            saving={invoiceEmails.isPending || !!groupLock}
            onChange={(v) =>
              invoiceEmails.mutate(
                { customer_id: customer.id, enabled: v },
                { onSuccess: afterMutate },
              )
            }
          />
          <FeatureToggle
            icon={<CreditCard size={14} />}
            label="Chain account — no card on file required"
            desc={
              groupLock
                ? `Locked on — a store of ${groupLock.masterName} bills centrally, so the card-on-file rule never applies`
                : 'Exempts this account from the card-on-file rule at onboarding (chains bill centrally). Everyone else must keep a card on file for rentals.'
            }
            enabled={groupLock ? true : customer.features?.card_on_file_exempt === true}
            saving={features.isPending || !!groupLock}
            onChange={(v) =>
              features.mutate(
                { customer_id: customer.id, features: { card_on_file_exempt: v } },
                { onSuccess: afterMutate },
              )
            }
          />
        </div>
      </div>

      {/* Phone directory — caller recognition + text opt-in state */}
      <PhoneDirectoryPanel customerId={customer.id} />

      {/* Returned / bounced payment workflow */}
      <ReturnedPaymentPanel customerId={customer.id} onRecorded={afterMutate} />

      {/* Billing comms editor */}
      <BillingCommsEditor
        key={customer.id}
        groupLock={groupLock}
        source={customer}
        saving={billingComms.isPending}
        onSave={(input) =>
          billingComms.mutate(
            { customer_id: customer.id, ...input },
            { onSuccess: afterMutate },
          )
        }
        onSendStatement={(to) =>
          sendStatement.mutate(
            { customerId: customer.id, to },
            {
              onSuccess: (res) =>
                setStatementResult({
                  sent: res.sent,
                  open_count: res.open_count,
                  open_total: res.open_total,
                  skipped_reason: res.skipped_reason,
                  test_send: res.test_send,
                  recipients: res.recipients,
                }),
            },
          )
        }
        sendingStatement={sendStatement.isPending}
        statementResult={statementResult}
      />

      {/* Communications history — every email this customer was sent, who
          triggered it, and when. */}
      <CommsHistoryPanel customerId={customer.id} />
    </div>
  );
}
